import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Users, ShoppingCart, DollarSign, TrendingUp, Eye, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFinanceiro = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, customers, orders
  
  const [stats, setStats] = useState({
    total_customers: 0,
    total_orders: 0,
    paid_orders: 0,
    pending_orders: 0,
    failed_orders: 0,
    total_revenue: 0
  });
  
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchData();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [statusFilter, orders]);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchData = async () => {
    try {
      const [statsRes, customersRes, ordersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/financial-stats`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/admin/customers`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/admin/orders`, getAuthHeaders())
      ]);

      setStats(statsRes.data);
      setCustomers(customersRes.data);
      setOrders(ordersRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    if (statusFilter === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter(order => order.payment_status === statusFilter));
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/orders/${orderId}`,
        getAuthHeaders()
      );
      setOrderDetails(response.data);
    } catch (error) {
      console.error('Erro ao buscar detalhes do pedido:', error);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    if (!confirm('Deseja realmente alterar o status deste pedido?')) {
      return;
    }

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/orders/${orderId}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      alert('Status do pedido atualizado com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status do pedido');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Tem certeza que deseja excluir este pedido? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/orders/${orderId}`,
        getAuthHeaders()
      );
      alert('Pedido excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir pedido:', error);
      alert('Erro ao excluir pedido');
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setShowEditModal(true);
  };

  const handleSaveCustomer = async (customerData) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/customers/${editingCustomer.id}`,
        customerData,
        getAuthHeaders()
      );
      alert('Cliente atualizado com sucesso!');
      setShowEditModal(false);
      setEditingCustomer(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      alert(error.response?.data?.detail || 'Erro ao atualizar cliente');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!confirm('Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita e todos os dados serão permanentemente removidos.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/customers/${customerId}`,
        getAuthHeaders()
      );
      alert('Cliente excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      alert('Erro ao excluir cliente');
    }
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { label: 'Pago', className: 'bg-green-100 text-green-800' },
      pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      failed: { label: 'Falhou', className: 'bg-red-100 text-red-800' },
      refunded: { label: 'Reembolsado', className: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Carregando dados financeiros...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="mb-2 hover:text-[#00A86B]"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar ao Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-gray-800">Financeiro</h1>
            <p className="text-gray-600 mt-1">Gestão de clientes, pedidos e receitas</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total de Clientes</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total_customers}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users size={24} className="text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total de Pedidos</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total_orders}</p>
                <p className="text-xs text-green-600 mt-1">{stats.paid_orders} pagos</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <ShoppingCart size={24} className="text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pedidos Pendentes</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending_orders}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <TrendingUp size={24} className="text-yellow-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Receita Total</p>
                <p className="text-2xl font-bold text-[#00A86B]">R$ {formatCurrency(stats.total_revenue)}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <DollarSign size={24} className="text-[#00A86B]" />
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-[#00A86B] text-[#00A86B]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Visão Geral
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'orders'
                    ? 'border-[#00A86B] text-[#00A86B]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pedidos ({orders.length})
              </button>
              <button
                onClick={() => setActiveTab('customers')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'customers'
                    ? 'border-[#00A86B] text-[#00A86B]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Clientes ({customers.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Resumo de Pedidos por Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Pagos</p>
                  <p className="text-2xl font-bold text-green-600">{stats.paid_orders}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.total_orders > 0 ? ((stats.paid_orders / stats.total_orders) * 100).toFixed(1) : 0}% do total
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending_orders}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.total_orders > 0 ? ((stats.pending_orders / stats.total_orders) * 100).toFixed(1) : 0}% do total
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Falhados</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failed_orders}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.total_orders > 0 ? ((stats.failed_orders / stats.total_orders) * 100).toFixed(1) : 0}% do total
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Últimos Pedidos</h3>
              <div className="space-y-3">
                {orders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{order.project_name}</p>
                      <p className="text-sm text-gray-600">{order.customer_name} • {order.customer_email}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(order.created_at)}</p>
                    </div>
                    <div className="text-right mr-4">
                      <p className="font-bold text-lg text-gray-800">R$ {formatCurrency(order.total)}</p>
                      <p className="text-xs text-gray-500">{order.items.length} item(s)</p>
                    </div>
                    {getStatusBadge(order.payment_status)}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            {/* Filter */}
            <Card className="p-4 mb-6">
              <div className="flex items-center gap-4">
                <Filter size={20} className="text-gray-600" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A86B]"
                >
                  <option value="all">Todos os Status</option>
                  <option value="paid">Pagos</option>
                  <option value="pending">Pendentes</option>
                  <option value="failed">Falhados</option>
                </select>
                <span className="text-sm text-gray-600">
                  {filteredOrders.length} pedido(s) encontrado(s)
                </span>
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pedido
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Projeto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-gray-900">#{order.id.substring(0, 8)}</p>
                          <p className="text-xs text-gray-500">{order.items.length} item(s)</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{order.customer_name}</p>
                          <p className="text-xs text-gray-500">{order.customer_email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">{order.project_name}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-gray-900">{formatDate(order.created_at)}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-semibold text-gray-900">R$ {formatCurrency(order.total)}</p>
                          {order.discount > 0 && (
                            <p className="text-xs text-green-600">Desconto: R$ {formatCurrency(order.discount)}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(order.payment_status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedOrder(order.id);
                                fetchOrderDetails(order.id);
                              }}
                            >
                              <Eye size={16} className="mr-1" />
                              Detalhes
                            </Button>
                            <select
                              value={order.payment_status}
                              onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                              className="text-xs border rounded px-2 py-1"
                            >
                              <option value="pending">Pendente</option>
                              <option value="paid">Pago</option>
                              <option value="failed">Falhou</option>
                              <option value="cancelled">Cancelado</option>
                              <option value="refunded">Reembolsado</option>
                            </select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteOrder(order.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              🗑️
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CPF
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cidade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Cadastro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">{customer.name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{customer.email}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900">{customer.cpf}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900">{customer.phone}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900">{customer.address.city}/{customer.address.state}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900">{formatDate(customer.created_at)}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditCustomer(customer)}
                          >
                            ✏️ Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteCustomer(customer.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            🗑️ Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Order Details Modal */}
        {selectedOrder && orderDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Detalhes do Pedido</h2>
                  <p className="text-gray-600">#{selectedOrder.substring(0, 8)}</p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSelectedOrder(null);
                    setOrderDetails(null);
                  }}
                >
                  ✕
                </Button>
              </div>

              {/* Order Info */}
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3">Informações do Pedido</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    {getStatusBadge(orderDetails.order.payment_status)}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Data</p>
                    <p className="font-medium">{formatDate(orderDetails.order.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Projeto</p>
                    <p className="font-medium">{orderDetails.order.project_name}</p>
                  </div>
                  {orderDetails.order.mp_preference_id && (
                    <div>
                      <p className="text-sm text-gray-600">Mercado Pago Preferência</p>
                      <p className="text-xs font-mono">{orderDetails.order.mp_preference_id.substring(0, 20)}...</p>
                    </div>
                  )}
                  {orderDetails.order.mp_payment_id && (
                    <div>
                      <p className="text-sm text-gray-600">Mercado Pago Payment ID</p>
                      <p className="text-xs font-mono">{orderDetails.order.mp_payment_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Info */}
              {orderDetails.customer && (
                <div className="mb-6">
                  <h3 className="font-bold text-lg mb-3">Dados do Cliente</h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <p><span className="font-medium">Nome:</span> {orderDetails.customer.name}</p>
                    <p><span className="font-medium">Email:</span> {orderDetails.customer.email}</p>
                    <p><span className="font-medium">CPF:</span> {orderDetails.customer.cpf}</p>
                    <p><span className="font-medium">Telefone:</span> {orderDetails.customer.phone}</p>
                    <p><span className="font-medium">Endereço:</span> {orderDetails.customer.address.street}, {orderDetails.customer.address.number} - {orderDetails.customer.address.neighborhood}, {orderDetails.customer.address.city}/{orderDetails.customer.address.state}</p>
                  </div>
                </div>
              )}

              {/* Items */}
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3">Itens do Pedido</h3>
                <div className="space-y-2">
                  {orderDetails.order.items.map((item, index) => (
                    <div key={index} className="flex justify-between p-3 bg-gray-50 rounded">
                      <span>{item.service_name}</span>
                      <span className="font-semibold">R$ {formatCurrency(item.price)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>R$ {formatCurrency(orderDetails.order.subtotal)}</span>
                  </div>
                  {orderDetails.order.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Desconto:</span>
                      <span>- R$ {formatCurrency(orderDetails.order.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold pt-2 border-t">
                    <span>Total:</span>
                    <span className="text-[#00A86B]">R$ {formatCurrency(orderDetails.order.total)}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Edit Customer Modal */}
        {showEditModal && editingCustomer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Editar Cliente</h2>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCustomer(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    handleSaveCustomer({
                      name: formData.get('name'),
                      cpf: formData.get('cpf'),
                      phone: formData.get('phone'),
                      email: formData.get('email')
                    });
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingCustomer.name}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A86B]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CPF
                    </label>
                    <input
                      type="text"
                      name="cpf"
                      defaultValue={editingCustomer.cpf}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A86B]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      name="phone"
                      defaultValue={editingCustomer.phone}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A86B]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      defaultValue={editingCustomer.email}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A86B]"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingCustomer(null);
                      }}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-[#00A86B] hover:bg-[#00A86B]/90"
                    >
                      Salvar Alterações
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFinanceiro;
